import {NumberInput, Select, TextInput} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {produce} from 'immer';
import React from 'react';
import type {FormFieldTypeV2, IFormFieldV2} from 'src/types/FormV2';
import {
  TEXT_MAX_LENGTH_ABSOLUTE,
  TEXT_MAX_LENGTH_DEFAULT,
} from 'src/types/FormV2';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';
import {useFormBuilderRegistry} from '../../../registry';

/**
 * Lightweight general settings panel: label, type, required, help text,
 * field width, and the type-specific bits (multiline/maxLength for text,
 * min/max for number) — raw Mantine inputs over local field state, no
 * `ProactForm`/`SectionValidationProvider`/notification-rule coupling.
 * Intended for embedders that don't need the Forms module's full
 * `GeneralSettings` (which hard-requires those).
 */
export const BasicFieldSettings = React.memo(() => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();
  const {fieldTypes} = useFormBuilderRegistry();

  if (!field) return null;

  const patchField = (patch: Partial<IFormFieldV2>) => {
    updateSelectedField(
      produce(field, draft => {
        Object.assign(draft, patch);
      })
    );
  };

  const patchValidation = (
    patch: Partial<NonNullable<IFormFieldV2['validation']>>
  ) => {
    updateSelectedField(
      produce(field, draft => {
        draft.validation = {...draft.validation, ...patch};
      })
    );
  };

  const patchTextboxOptions = (
    patch: Partial<NonNullable<IFormFieldV2['textboxOptions']>>
  ) => {
    updateSelectedField(
      produce(field, draft => {
        draft.textboxOptions = {...draft.textboxOptions, ...patch};
      })
    );
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Label"
        placeholder="Enter field label"
        value={field.label}
        onChange={e => patchField({label: e.currentTarget.value})}
        maxLength={1024}
        error={field.label?.trim() ? undefined : 'Label is required'}
      />

      <Select
        label="Field Type"
        placeholder="Select a field type"
        data={fieldTypes.map(opt => ({
          value: String(opt.id),
          label: opt.label,
        }))}
        value={field.type}
        onChange={val => val && patchField({type: val as FormFieldTypeV2})}
        clearable={false}
      />

      <Switch
        label="Required"
        checked={!!field.required}
        onChange={e => patchField({required: e.currentTarget.checked})}
      />

      <TextInput
        label="Help Text"
        placeholder="Additional guidance shown beside the label"
        value={field.labelTooltip ?? ''}
        onChange={e => patchField({labelTooltip: e.currentTarget.value})}
        maxLength={255}
      />

      <Stack gap={4}>
        <Text size="sm" fw={500}>
          Field Width
        </Text>
        <SegmentedControl
          value={field.size ?? 'xl'}
          onChange={val => patchField({size: val as IFormFieldV2['size']})}
          data={[
            {label: 'Small', value: 'sm'},
            {label: 'Medium', value: 'md'},
            {label: 'Large', value: 'xl'},
          ]}
        />
      </Stack>

      {field.type === 'text' && (
        <>
          <Switch
            label="Multiline"
            checked={!!field.textboxOptions?.multiline}
            onChange={e =>
              patchTextboxOptions({multiline: e.currentTarget.checked})
            }
          />
          <NumberInput
            label="Max Length"
            placeholder="Maximum allowed characters"
            value={field.validation?.maxLength ?? TEXT_MAX_LENGTH_DEFAULT}
            onChange={val =>
              patchValidation({
                maxLength: typeof val === 'number' ? val : undefined,
              })
            }
            min={1}
            max={TEXT_MAX_LENGTH_ABSOLUTE}
            clampBehavior="strict"
          />
        </>
      )}

      {field.type === 'number' && (
        <Group grow>
          <NumberInput
            label="Minimum Value"
            value={field.validation?.min}
            onChange={val =>
              patchValidation({min: typeof val === 'number' ? val : undefined})
            }
          />
          <NumberInput
            label="Maximum Value"
            value={field.validation?.max}
            onChange={val =>
              patchValidation({max: typeof val === 'number' ? val : undefined})
            }
          />
        </Group>
      )}
    </Stack>
  );
});
